/* eslint-disable no-magic-numbers */

import { capitalize, deburr, sortBy, uniq, upperFirst } from 'lodash';
import { endOfDay, endOfMonth, format, parse } from 'date-fns';

import {
  AccountCategory,
  Consolidation,
  EliminationGroupItem,
  OrganisationConfigItem
} from '@futrli-sdk/consolidations';
import { Organisation } from '@futrli-sdk/organisations';
import { CoaStructureTypes } from '@futrli/organisations/core/types';
import { UITreeNode } from '@futrli/ui-components';

import {
  ConsolidationConfig,
  CreateConsolidationParams,
  FetchCurrencyRatesResponseData,
  FxRateOverride,
  OffsetEliminationGroup
} from '../../core/api/consolidation/types';
import {
  COACategory,
  COACategoryId,
  COAAccount,
  DailyFxRatesOverrideFormValues,
  DailyFxRatesGridData,
  DailyFxDataValues,
  ExtendedOrganisation
} from './types';
import { EliminationsFormValues } from './useEliminationsForm';
import { getDateKeysInRange, getMonthKeysInRange } from '../../core/utils';
import { accountCategoriesInOrder } from '../../../../predict/src/forecast/prediction-builder/types';
import { minDecimalPlaces, roundToDp } from '@futrli/shared-services';

/**
 * Returns a new list of elimination groups with the specified account added to the specified group
 * @param groups - list of eliminations groups
 * @param group - the elimination group to add the account to
 * @param accountId - the account id to add to the specified group
 */
export const addEliminationGroupAccountToList = (
  groups: Record<string, OffsetEliminationGroup[]>,
  group: OffsetEliminationGroup,
  accountId: string | number
) => {
  const categoryName = group.category;

  const newCategoryEliminationGroups = [...groups[categoryName]];
  const index = groups[categoryName].indexOf(group);

  // Update elimination, filtering out account to be removed
  newCategoryEliminationGroups[index] = {
    ...newCategoryEliminationGroups[index],
    accounts: [...newCategoryEliminationGroups[index].accounts, accountId.toString()]
  };

  const newEliminations = {
    ...groups,
    [categoryName]: newCategoryEliminationGroups
  };

  return newEliminations;
};

/**
 * Takes a COAAccount[] and converts it to an array of UITreeNode.
 * It can also optionlly take a list of account IDs to filter out.
 * @param coa - COAAccount[] input
 * @param filterAccountIds - Optional list of account IDs to filter out
 */
export const convertFullCOAToTreeNodes = (coa: COAAccount[], filterAccountIds: string[] = []) => {
  // Create category index
  const categories: Partial<Record<COACategoryId, COAAccount[]>> = {};

  // Add children to the category index (include filtering)
  coa.forEach(item => {
    if (!filterAccountIds.includes(item.id)) {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category]!.push(item);
    }
  });

  // Filter out empty categories
  Object.keys(categories).forEach(category => {
    const c = category as COACategoryId;
    if (!categories[c]?.length) {
      delete categories[c];
    }
  });

  // Convert categories to tree nodes
  return Object.keys(categories).map(category => {
    const c = category as COACategoryId;
    return convertCOACategoryToTreeNodes(c, categories[c]!);
  });
};

/**
 * Takes a COA category Id and its COAAccounts[] and converts it to a UITreeNode group (with children)
 * @param category - Category ID
 * @param accounts - The accounts that are children of this category
 */
export const convertCOACategoryToTreeNodes = (category: COACategoryId, accounts: COAAccount[]) => {
  const sortedAccounts = sortBy(accounts, ['name', 'code']);
  const label = capitalize(category.replace(/_/g, ' '));

  const output: UITreeNode<COACategory | COAAccount> = {
    label,
    id: category,
    type: 'group',
    children: sortedAccounts.map(account => convertCOAAccountToTreeNode(account)),
    extra: { category, label, accounts }
  };
  return output;
};

/**
 * Takes a COAAccount and converts it to a UITreeNode option
 * @param account - Account to convert
 */
export const convertCOAAccountToTreeNode = (account: COAAccount) => {
  const output: UITreeNode<COAAccount> = {
    label: `${account.name}: ${account.code}`,
    id: account.id,
    type: 'option',
    extra: account
  };
  return output;
};

/**
 * Takes a Consolidation data object (from backend) and returns a ConsolidationConfig item
 * @param config - Source consolidation data object
 */
export const convertConsolidationDataToConfig = (data: Consolidation) => {
  const eliminations: Record<string, OffsetEliminationGroup[]> = {};
  let fxRateOverrides: FxRateOverride[] = [];

  data.eliminations.forEach(elim => {
    if (!eliminations[elim.category]) {
      eliminations[elim.category] = [];
    }

    eliminations[elim.category].push({
      name: elim.name,
      category: elim.category,
      accounts: elim.accounts.map(acc => acc.account_id)
    });
  });

  if (data.fx_overrides) {
    fxRateOverrides = data.fx_overrides.map(override => ({
      currencyFrom: override.currency_from,
      currencyTo: override.currency_to,
      dateFrom: parse(override.override_from, 'yyyy-MM-dd', new Date()),
      dateTo: endOfDay(parse(override.override_to, 'yyyy-MM-dd', new Date())),
      dailyRate: override.rate
    }));
  }

  const output: ConsolidationConfig = {
    id: data.id,
    name: data.name,
    yearEnd: `${new Date().getFullYear()}/${data.financial_year_end_month}/${data.financial_year_end_day}`,
    coaStructure: data.account_structure as CoaStructureTypes,
    presentationCurrency: data.display_currency,
    organisations: data.organisations.map(org => ({
      id: org.organisation_id,
      code: org.short_code
    })),
    eliminations,
    fxRateOverrides
  };

  return output;
};

/**
 * Converts the Fx data from the backend to the format we need
 * @param data - The data received from fetchCurrencyRates
 */
export const convertFxDataResponseToCurencyRateData = (data: FetchCurrencyRatesResponseData) => {
  const values: DailyFxDataValues = {};

  Object.keys(data).forEach(currencyCode => {
    const currencyData = data[currencyCode];
    if (!values[currencyCode]) {
      values[currencyCode] = {};
    }

    currencyData.forEach(fxRate => {
      values[currencyCode][fxRate.date.replace(/-/g, '/')] = fxRate.original;
    });
  });

  return values;
};

/**
 * Takes the daily FX exchange rates and override ranges and generates grid data from them
 * @param startDate - The date to start from
 * @param endDate - The date to end from
 * @param exchangeRates - The daily exhange rate data for the selected currency
 * @param formData - Override form data for the selected currency
 */
export const generateDailyFxRatesGridData = (
  startDate: Date,
  endDate: Date,
  exchangeRates: Record<string, string>,
  formData: DailyFxRatesOverrideFormValues
) => {
  const overriddenValues = generateOverriddenDailyFxRateValues(formData);
  const dateRange = getMonthKeysInRange(startDate, endDate);

  const output: DailyFxRatesGridData[] = dateRange.map(month => {
    const date = month.date;
    const endDay = endOfMonth(date).getDate();
    const values: DailyFxRatesGridData['values'] = {};

    let dayDateKey = '';
    let groupOverride = '';
    let dayValue = '';

    for (let i = 1; i <= endDay; i++) {
      dayDateKey = format(new Date(date.getFullYear(), date.getMonth(), i), 'yyyy/MM/dd');
      groupOverride = overriddenValues[dayDateKey];
      dayValue = groupOverride || exchangeRates[dayDateKey] || '';

      values[i] = {
        title: dayValue ? minDecimalPlaces(trimDecimalZeroes(dayValue), 3) : undefined,
        value: dayValue ? roundToDp(dayValue, 3) : '',
        highlight: !!groupOverride
      };
    }

    return {
      date: new Date(month.id),
      values
    };
  });

  return output;
};

/**
 * Takes the daily FX rate override ranges form data and generates an object with override values
 * for each day in the given ranges.
 * @param data - Daily FX rates form data
 */
export const generateOverriddenDailyFxRateValues = (data: DailyFxRatesOverrideFormValues) => {
  const values: Record<string, string> = {};

  data.overrides.forEach(override => {
    const dateRange = getDateKeysInRange(override.dateFrom, override.dateTo);

    dateRange.forEach(date => {
      values[date.id] = override.dailyRate;
    });
  });

  return values;
};

/**
 * Generates a random character string
 */
export const generateRandomChars = (length = 3) => {
  const chars = [];
  let output = '';

  for (let i = 65; i < 90; i++) {
    chars.push(String.fromCharCode(i));
  }
  for (let i = 0; i < length; i++) {
    output += chars[Math.floor(Math.random() * chars.length)];
  }
  return output;
};

/**
 * Generates a unique 3-letter code for the given organisation name. This is a case insensitive check.
 * @param orgName - The name of the organisation to generate a code for
 * @param existingCodes - An array of existing organisation codes. Specifying this will ensure you get a unique code.
 */
export const generateUniqueEntityCode = (orgName: string, existingCodes: string[] = []) => {
  let code = '';
  const cleanOrgName = [...deburr(orgName).matchAll(/[a-zA-Z0-9 ]/g)]
    .map(match => match[0])
    .join('')
    .toUpperCase();
  const cleanOrgNameNoSpaces = cleanOrgName.replace(/ /g, '');
  const words = cleanOrgName.split(' ').filter(word => !!word.length);
  const upperExistingCodes = existingCodes.map(c => c.toUpperCase());

  // Try to abbreviate / use the first 3 letters of the org name / use a random 3 char string
  if (words.length >= 3) {
    code = `${words[0][0]}${words[1][0]}${words[2][0]}`;
  } else if (cleanOrgNameNoSpaces.length >= 3) {
    code = cleanOrgNameNoSpaces.substring(0, 3);
  } else {
    code = generateRandomChars(3).toUpperCase();
  }

  // If we generated a 3 char code but it already exists, try to replace the last char with 1-9
  if (upperExistingCodes.includes(code)) {
    for (let i = 1; i <= 9; i++) {
      code = `${code.substring(0, 2)}${i}`;
      if (!upperExistingCodes.includes(code)) {
        break;
      }
    }
  }

  // If replacing the last char with 1-9 still leaves us with a clash, use a random 3-letter code
  if (upperExistingCodes.includes(code)) {
    code = generateRandomChars(3).toUpperCase();
    while (upperExistingCodes.includes(code)) {
      code = generateRandomChars(3).toUpperCase();
    }
  }

  return code;
};

/**
 * Returns the list of unique categories in the COA
 * @param coa - COA to get the categories from
 */
export const getCOACategories = (coa: COAAccount[]) => {
  const uniqueCategoryIds = uniq(coa.map(item => item.category));
  return uniqueCategoryIds.map(id => ({
    id,
    label: upperFirst(id.replace(/_/g, ' ').toLowerCase())
  }));
};

/**
 * Takes the eliminationGroups data used by the UI and converts it to the format needed for the save request
 * @param eliminationsGroups - the elimination groups data to convert
 */
export const getEliminationsRequestData = (eliminationsGroups: Record<string, OffsetEliminationGroup[]>) => {
  const output: CreateConsolidationParams['eliminations'] = [];

  Object.keys(eliminationsGroups).forEach(category => {
    const elimGroupsInCategory = eliminationsGroups[category];
    elimGroupsInCategory.forEach(eg => {
      output.push({
        name: eg.name,
        category: eg.category as EliminationGroupItem['category'],
        accounts: eg.accounts.map(acc => ({
          account_id: acc
        }))
      });
    });
  });

  return output;
};

/**
 * Calculates and returns the start and end date for the daily FX rate grid based upon
 * the given year end date
 * @param yearEnd - The year end date for the consolidation
 */
export const getStartEndGridDates = (yearEnd: Date) => {
  const now = new Date();
  const todayStr = `${now.getFullYear()}/${now.getMonth() + 1}/${now.getDate()}`;
  const today = new Date(todayStr);
  const financialYearEndsToday =
    `${yearEnd.getMonth()}/${yearEnd.getDate()}` === `${today.getMonth()}/${today.getDate()}`;

  const day = `0${yearEnd.getDate()}`.slice(-2);
  const month = `0${yearEnd.getMonth() + 1}`.slice(-2);

  const startDateStr = `${today.getFullYear() - (financialYearEndsToday ? 3 : 2)}/${month}/${day}`;
  const startDate = new Date(startDateStr);
  const endDateStr = `${today.getFullYear() + 3}/${month}/${day}`;
  const endDate = new Date(endDateStr);

  return {
    startDate,
    startDateStr,
    endDate,
    endDateStr
  };
};

/**
 * Returns a list of unique currency codes for the given organisations
 * @param organisations - list of organisations to process
 */
export const getUniqueCurrencyCodes = (organisations: OrganisationConfigItem[]) => {
  return uniq(organisations.map(org => org.currency_code));
};

/**
 * Returns true if the given organisations meet the minimum count requirement for a consolidation.
 * Normally at least 2 organisations are required, but a single organisation is allowed
 * when its currency differs from the consolidation's presentation currency.
 * @param organisations - The list of selected organisations
 * @param presentationCurrency - The selected presentation currency
 */
export const hasEnoughOrganisations = (organisations: OrganisationConfigItem[], presentationCurrency: string) => {
  if (organisations.length > 1) {
    return true;
  }
  if (organisations.length === 1 && !!presentationCurrency) {
    return organisations[0].currency_code !== presentationCurrency;
  }
  return false;
};

/**
 * Returns true or false depending upon if the given params are a multi-currency configuration or not
 * @param organisations - The list of selected organisations
 * @param presentationCurrency - The selected presentation currency
 */
export const isMultiCurrency = (organisations: OrganisationConfigItem[], presentationCurrency: string) => {
  const currencyCodes = uniq([...getUniqueCurrencyCodes(organisations), presentationCurrency]);
  return currencyCodes.filter(code => !!code).length > 1;
};

/**
 * Takes an array of organisations, organisationConfigData and an optional ConsolidationOrganisation
 * array and returns an array of merged data
 * @param organisations - List of organisations
 * @param extraOrgsData - List of extra data for the given organisations
 * @param consolidationOrgs - Optional list of consolidation organisations. Used for `code`
 */
export const mergeOrganisationData = (organisations: Organisation[], extraOrgsData: OrganisationConfigItem[]) => {
  const output: ExtendedOrganisation[] = organisations.map(org => {
    const extra = extraOrgsData.find(ex => ex.id === org.id) || ({} as OrganisationConfigItem);

    return {
      ...org,
      ...extra,
      name: extra.name || org.name,
      can_be_added_to_consolidation: extra?.id ? extra.can_be_added_to_consolidation : false
    };
  });

  return output;
};

/**
 * Returns a new list of elimination groups with the specified account removed from the specified group
 * @param groups - list of eliminations groups
 * @param group - the elimination group to remove the account from
 * @param accountId - the account id to remove from the specified group
 */
export const removeEliminationGroupAccountFromList = (
  groups: Record<string, OffsetEliminationGroup[]>,
  accountId: string | number
) => {
  const group = Object.values(groups)
    .flat()
    .find(group => group.accounts.includes(accountId.toString()));
  if (!group) {
    return groups;
  }

  const categoryName = group.category;

  const newCategoryEliminationGroups = [...groups[categoryName]];
  const index = groups[categoryName].indexOf(group);

  // Update elimination, filtering out account to be removed
  newCategoryEliminationGroups[index] = {
    ...newCategoryEliminationGroups[index],
    accounts: group.accounts.filter(accId => accId !== accountId)
  };

  const newEliminations = {
    ...groups,
    [categoryName]: newCategoryEliminationGroups
  };

  return newEliminations;
};

/**
 * Returns a new list of elimination groups with the specified group removed
 * @param groups
 * @param groupToRemove
 */
export const removeEliminationGroupFromList = (
  groups: Record<string, OffsetEliminationGroup[]>,
  groupToRemove: OffsetEliminationGroup
) => {
  const categoryName = groupToRemove.category;

  // Filter out elimination groups that have no accounts
  const newCategoryEliminationGroups = [...groups[categoryName]].filter(elimGroup => {
    return elimGroup !== groupToRemove;
  });

  const newEliminations = {
    ...groups,
    [categoryName]: newCategoryEliminationGroups
  };

  // Filter out any categories that have no eliminations
  Object.keys(newEliminations).forEach(catName => {
    if (!newEliminations[catName].length) {
      delete newEliminations[catName];
    }
  });

  return newEliminations;
};

/**
 * Takes a string representation of a decimal number and returns one that has the trailing zeroes trimmed
 * @param num - The string representation of a number to process
 */
export const trimDecimalZeroes = (num: string) => {
  let endIndex = -1;

  if (isNaN(parseFloat(num)) || num.indexOf('.') === -1) {
    return num;
  }

  for (let i = num.length; i > 0; i--) {
    if (num[i - 1] !== '0' && num[i - 1] !== '.') {
      endIndex = i;
      break;
    }
  }

  if (endIndex === -1) {
    return '0';
  } else {
    return num.substring(0, endIndex);
  }
};

/**
 * Returns a new list of eliminations groups with the specified group updated with the specified data
 * @param groups - source eliminations group list
 * @param groupToEdit - group to update with the given form data
 * @param formData - the form data to update the specified group with
 */
export const updateEliminationGroup = (
  groups: Record<string, OffsetEliminationGroup[]>,
  groupToEdit: OffsetEliminationGroup,
  formData: EliminationsFormValues
) => {
  const newData = { ...groups };

  if (!newData[formData.category]) {
    newData[formData.category] = [];
  }

  // Find the old elimination group index
  const oldCategory = groupToEdit.category;
  const index = newData[oldCategory].findIndex(elimGroup => {
    return elimGroup === groupToEdit;
  });

  // If found...
  if (index !== -1) {
    // Update group with new form data
    newData[formData.category][index] = {
      ...newData[oldCategory][index],
      ...formData
    };

    // If the group category has changed...
    if (formData.category !== oldCategory) {
      // Remove the group data from the old category
      newData[oldCategory].splice(index, 1);

      // If the category is now empty, remove it
      if (!newData[oldCategory].length) {
        delete newData[oldCategory];
      }
    }
  }

  return newData;
};

/**
 * Sorts COA nodes array in hard coded order of categories.
 * @param nodes COA tree / categories array that needs to be sorted
 */
export const sortCoaCategories = <T extends { id: string }>(nodes: T[]) => {
  return nodes.sort((nodeA: T, nodeB: T): number => {
    const categoryAIndex = accountCategoriesInOrder.indexOf(nodeA.id as AccountCategory);
    const categoryBIndex = accountCategoriesInOrder.indexOf(nodeB.id as AccountCategory);

    if (categoryAIndex === -1) {
      return 1;
    }
    if (categoryBIndex === -1) {
      return -1;
    }

    return categoryAIndex - categoryBIndex;
  });
};
